import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person,
  Save,
  Cancel,
  PhotoCamera,
  Edit
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import uploadService from '../../services/uploadService';
import providerService from '../../services/providerService';

const ClientProfile = ({ embedded = false }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    avatarUrl: '',
    // Social linkek (csak PROVIDER-eknek)
    website: '',
    facebookUrl: '',
    instagramUrl: '',
    tiktokUrl: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    if (user) {
      // Ha PROVIDER, betöltjük a friss provider adatokat a DB-ből
      if (user?.provider?.id) {
        providerService.getProviderDetails(user.provider.id).then(response => {
          if (response.success) {
            const provider = response.data.provider;
            setFormData({
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              phone: user.phone || '',
              avatarUrl: user.avatarUrl || '',
              // Social linkek (csak PROVIDER-eknek)
              website: provider.website || '',
              facebookUrl: provider.facebookUrl || '',
              instagramUrl: provider.instagramUrl || '',
              tiktokUrl: provider.tiktokUrl || ''
            });
          }
        }).catch(err => console.error('Provider adatok betöltése hiba:', err));
      } else {
        setFormData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || '',
          avatarUrl: user.avatarUrl || '',
          website: '',
          facebookUrl: '',
          instagramUrl: '',
          tiktokUrl: ''
        });
      }
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validáció
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        throw new Error('Név mezők kitöltése kötelező');
      }

      const userUpdateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      };
      const response = await userService.updateProfile(userUpdateData);

      if (!response.success) {
        throw new Error(response.message || 'Profil frissítése sikertelen');
      }
      updateUser(response.data.user);

      // Ha PROVIDER, frissítjük a social linkeket is
      if (user?.provider?.id) {
        const providerUpdateData = {
          website: formData.website,
          facebookUrl: formData.facebookUrl,
          instagramUrl: formData.instagramUrl,
          tiktokUrl: formData.tiktokUrl
        };
        const providerResponse = await providerService.updateProvider(user.provider.id, providerUpdateData);
        if (!providerResponse.success) {
          throw new Error(providerResponse.message || 'Szolgáltatói adatok frissítése sikertelen');
        }
        updateUser({ ...response.data.user, provider: providerResponse.data.provider });
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Profil mentése hiba:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validáció
      if (!passwordData.currentPassword) {
        throw new Error('Jelenlegi jelszó megadása kötelező');
      }

      if (!passwordData.newPassword) {
        throw new Error('Új jelszó megadása kötelező');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('Az új jelszónak legalább 6 karakter hosszúnak kell lennie');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('Az új jelszavak nem egyeznek');
      }

      const response = await userService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Jelszó változtatás sikertelen');
      }
    } catch (error) {
      console.error('Jelszó változtatás hiba:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Fájl méret ellenőrzés (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('A kép mérete nem lehet nagyobb 10MB-nál');
      return;
    }

    // Fájl típus ellenőrzés
    if (!file.type.startsWith('image/')) {
      setError('Csak kép fájlok tölthetők fel');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Step 1: upload file to R2
      const uploadResponse = await uploadService.uploadImage(file, 'avatar', user.id);
      if (!uploadResponse.success) {
        throw new Error(uploadResponse.message || 'Kép feltöltése sikertelen');
      }
      const avatarUrl = uploadResponse.url;

      // Step 2: save URL to DB
      const saveResponse = await userService.updateUserAvatar(avatarUrl);
      if (!saveResponse.success) {
        throw new Error(saveResponse.message || 'Profilkép URL mentése sikertelen');
      }

      setFormData(prev => ({ ...prev, avatarUrl }));
      updateUser({ ...user, avatarUrl });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Avatar feltöltés hiba:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      {/* Hibaüzenetek és siker */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Adatok sikeresen mentve!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profilkép szekció */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Profilkép
              </Typography>
              
              <Avatar
                src={formData.avatarUrl || undefined}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {!formData.avatarUrl && <Person sx={{ fontSize: '3rem' }} />}
              </Avatar>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarUpload}
              />
              <label htmlFor="avatar-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  disabled={saving}
                  fullWidth
                >
                  Kép feltöltése
                </Button>
              </label>

              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Maximum 10MB, JPG/PNG formátum
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Alapadatok szekció */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alapadatok
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Keresztnév"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={saving}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Vezetéknév"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={saving}
                    required
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Telefonszám"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={saving}
                    placeholder="pl. +36301234567"
                  />
                </Grid>

                {/* Közösségi média linkek - csak PROVIDER-eknek */}
                {user?.provider && (
                  <>
                    <Grid size={12} sx={{ pt: 2 }}>
                      <Divider />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                        Közösségi média
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Weboldal"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        disabled={saving}
                        placeholder="https://example.com"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Facebook URL"
                        value={formData.facebookUrl}
                        onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                        disabled={saving}
                        placeholder="https://facebook.com/..."
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Instagram URL"
                        value={formData.instagramUrl}
                        onChange={(e) => handleInputChange('instagramUrl', e.target.value)}
                        disabled={saving}
                        placeholder="https://instagram.com/..."
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="TikTok URL"
                        value={formData.tiktokUrl}
                        onChange={(e) => handleInputChange('tiktokUrl', e.target.value)}
                        disabled={saving}
                        placeholder="https://tiktok.com/..."
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? 'Mentés...' : 'Mentés'}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/dashboard')}
                  disabled={saving}
                >
                  Mégse
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Jelszó változtatás szekció */}
        <Grid size={12}>
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Jelszó változtatása
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  disabled={saving}
                >
                  {showPasswordForm ? 'Elrejtés' : 'Szerkesztés'}
                </Button>
              </Box>

              {showPasswordForm && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Jelenlegi jelszó"
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                        disabled={saving}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Új jelszó"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                        disabled={saving}
                        required
                        helperText="Minimum 6 karakter"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Új jelszó megerősítése"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                        disabled={saving}
                        required
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                      onClick={handleChangePassword}
                      disabled={saving}
                    >
                      {saving ? 'Mentés...' : 'Jelszó változtatása'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  if (embedded) return content;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profil szerkesztése
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Itt szerkesztheti személyes adatait
        </Typography>
      </Box>
      {content}
    </Container>
  );
};

export default ClientProfile;